<script setup lang="ts">
import { carCount, houiList, priceRange, tsuboRange } from "~/constants/plan";
import { VueFinalModal } from "vue-final-modal";
import { getCategory } from "~/utils/sekou/category";

const route = useRoute();

const props = defineProps<{
  // 閉じる処理
  onClose: () => void;
}>();

// 分類
const cate = getCategory();
const planAttrs = await usePlanAttr();

// スタイル（新築・リフォーム）を除外
const realAttrs = planAttrs.value.filter((o) => ![1, 3].includes(o.category));

// 種別で使用する分類　新築・リフォームを分離
const syubetsuAttrs = planAttrs.value
  .filter((o) => o.category == 1 || o.category == 3)
  .map((o) => ({
    id: o.attr_num,
    name: o.cate_name,
  }));

// 種別で使用するattr_num
const syubetsuAttrNum = syubetsuAttrs.map((o) => o.id);

// それ以外の分類
const selectAttr = ref<number[]>(
  route.query.attr
    ? route.query.attr
        .toString()
        .split(",")
        .map((o) => parseInt(o))
        .filter((e) => !syubetsuAttrNum.includes(e))
    : [],
);

function toggleAttr({ attr_num }: { attr_num: number }) {
  if (false === selectAttr.value.includes(attr_num)) {
    selectAttr.value.push(attr_num);
  } else {
    selectAttr.value = selectAttr.value.filter((e) => e != attr_num);
  }
}

//  工事種別の分類を保存
const syubetsuAttr = ref<number | undefined>(
  route.query.attr
    ? route.query.attr
        .toString()
        .split(",")
        .map((o) => parseInt(o))
        .find((e) => syubetsuAttrNum.includes(e))
    : undefined,
);

// 価格範囲
const range = ref(
  route.query.range ? parseInt(route.query.range.toString()) : 0,
);

// 面積範囲
const tsubo = ref(
  route.query.tsubo ? parseInt(route.query.tsubo.toString()) : 0,
);

// 工事種別 新築 or リフォーム
const ktype = ref(
  route.query.ktype ? parseInt(route.query.ktype.toString()) : 0,
);

// 車台数
const kuruma = ref(
  route.query.kuruma ? parseInt(route.query.kuruma.toString()) : 0,
);

// 方位
const houi = ref(route.query.houi ? route.query.houi.toString() : "");

// query用のデーター
const queryData = computed(() => {
  const queryAttr = Array.from(selectAttr.value);

  if (syubetsuAttr.value) {
    queryAttr.push(syubetsuAttr.value);
  }

  return {
    range: range.value,
    tsubo: tsubo.value,
    ktype: ktype.value,
    kuruma: kuruma.value,
    houi: houi.value,
    attr: queryAttr.sort((a, b) => a - b).join(","),
    order: route.query.order ? route.query.order.toString() : "",
    asc: route.query.asc ? parseInt(route.query.asc.toString()) : 0,
    off: 0,
  };
});

const { data: pattern, pending } = useFetch("/api/plan/searchPattern", {
  query: queryData,
  watch: [queryData],
  key: "/api/plan/searchPattern/" + JSON.stringify(queryData.value),
});

function onSubmit() {
  useRouter().push({
    query: queryData.value,
  });
  props.onClose();
}

function reset() {
  range.value = 0;
  tsubo.value = 0;
  ktype.value = 0;
  kuruma.value = 0;
  houi.value = "";
  selectAttr.value = [];
  syubetsuAttr.value = undefined;
}
</script>

<template>
  <VueFinalModal
    content-class="absolute inset-0 "
    content-transition="vfm-slide-down"
    swipe-to-close="down"
    :threshold="200"
  >
    <div
      class="absolute inset-0 h-full pc:p-8 select-none"
      @click.self="onClose"
    >
      <div class="flex flex-col h-full max-w-5xl mx-auto bg-gray-100">
        <div class="sticky top-0 z-10">
          <button
            type="button"
            @click="onClose()"
            class="text-green-800 absolute right-2 top-2"
            aria-label="close"
          >
            <Icon size="32" name="mdi:close" />
          </button>

          <p class="text-center text-lg font-bold bg-gray-100 p-2">
            絞り込み検索
          </p>
        </div>
        <div class="flex flex-col h-full overflow-y-scroll p-4">
          <p class="font-bold mb-2">工事種別</p>
          <DropdownMenu
            class="dropdown mb-4"
            :size="18"
            :list="syubetsuAttrs"
            :selected="syubetsuAttr"
            :hasEmpty="true"
            @onSelectId="syubetsuAttr = $event"
          >
            <template v-slot:title>
              <p class="p-2">
                {{
                  syubetsuAttr
                    ? syubetsuAttrs.find((e) => e.id == syubetsuAttr)?.name
                    : "工種"
                }}
              </p>
            </template>
          </DropdownMenu>

          <p class="font-bold mb-2">価格帯</p>
          <DropdownMenu
            class="dropdown mb-4"
            :size="18"
            :list="priceRange"
            :selected="range"
            :hasEmpty="true"
            @onSelectId="range = $event"
          >
            <template v-slot:title>
              <p class="p-2">
                {{
                  range ? priceRange.find((e) => e.id == range)?.name : "価格帯"
                }}
              </p>
            </template>
          </DropdownMenu>

          <p class="font-bold mb-2">面積</p>
          <DropdownMenu
            class="dropdown mb-4"
            :size="18"
            :list="tsuboRange"
            :selected="tsubo"
            :hasEmpty="true"
            @onSelectId="tsubo = $event"
          >
            <template v-slot:title>
              <p class="p-2">
                {{
                  tsubo ? tsuboRange.find((e) => e.id == tsubo)?.name : "面積"
                }}
              </p>
            </template>
          </DropdownMenu>

          <p class="font-bold mb-2">車台数</p>
          <DropdownMenu
            class="dropdown mb-4"
            :size="18"
            :list="carCount"
            :selected="kuruma"
            :hasEmpty="true"
            @onSelectId="kuruma = $event"
          >
            <template v-slot:title>
              <p class="p-2">
                {{
                  kuruma ? carCount.find((e) => e.id == kuruma)?.name : "車台数"
                }}
              </p>
            </template>
          </DropdownMenu>

          <p class="font-bold mb-2">方位</p>
          <DropdownMenu
            class="dropdown mb-4"
            :size="18"
            :list="houiList"
            :selected="houi"
            :hasEmpty="true"
            @onSelectName="houi = $event"
          >
            <template v-slot:title>
              <p class="p-2">
                {{ houi ? houiList.find((e) => e.name == houi)?.name : "方位" }}
              </p>
            </template>
          </DropdownMenu>

          <p class="font-bold mb-2">分類</p>
          <div
            class=""
            v-for="(cate_name, category) in cate"
            :key="`${cate_name}-${category}`"
          >
            <div
              v-if="realAttrs.filter((o) => o.category == category).length"
              class="p-2 mb-2"
            >
              <p class="font-bold mb-2">
                <Icon name="mdi:tag" />
                {{ cate_name }}
              </p>
              <ul class="flex flex-wrap gap-4">
                <li
                  v-for="a in realAttrs.filter((o) => o.category == category)"
                  :key="`${cate_name}-${a.cate_name}`"
                >
                  <button
                    @click="toggleAttr(a)"
                    :class="
                      selectAttr.includes(a.attr_num) ? 'selected' : 'normal'
                    "
                    class="px-2 py-1 whitespace-nowrap border rounded disabled:opacity-20"
                  >
                    <!-- :disabled="a.disabled" -->
                    {{ a.cate_name }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          class="w-full p-2 sticky bottom-0 border-t drop-shadow bg-gray-100 flex justify-between pc:justify-center gap-4"
        >
          <button
            class="whitespace-nowrap px-4 py-2 text-sm hover:bg-gray-100 border rounded bg-white pc:absolute left-2 bottom-2"
            @click="reset"
          >
            リセット
          </button>
          <div class="w-full pc:w-1/3 relative">
            <p class="count_label">
              <template v-if="pattern && !pending">
                <span class="text-lg">{{ pattern.count }}</span>
                <span class="text-xs">件</span>
              </template>
              <template v-if="pending">
                <Icon class="animate-spin" name="mdi:loading" />
              </template>
            </p>

            <button
              class="flex items-center justify-center w-full text-white rounded py-3 bg-blue-600 hover:bg-blue-700"
              @click="onSubmit"
            >
              <Icon class="mr-2" size="24" name="mdi:magnify" />
              検索
            </button>
          </div>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<style lang="scss" scoped>
.count_label {
  @apply absolute bottom-full right-0 -translate-y-2 px-4 py-1 bg-white drop-shadow-lg text-blue-800 font-bold rounded;
  &::before {
    @apply absolute right-0 top-full -translate-x-2;
    content: "";
    border-top: 6px solid white;
    border-right: 6px solid white;
    border-bottom: 6px solid transparent;
    border-left: 6px solid transparent;
  }
}
.dropdown {
  @apply bg-white rounded border-b;
}
.normal {
  @apply hover:text-white hover:bg-gray-500 bg-white;
}

.selected {
  @apply bg-gray-500 text-white border-gray-500;
}
</style>
